import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc as firestoreDoc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getPatientHeaderPathSegments } from '../constants/adultAutismFirestorePaths';

/**
 * Trigger the full Adult Autism Patient Report render with a transactional guard.
 *
 * Uses `runTransaction` to atomically check whether the patient report is
 * already processing before setting `prReportProcessingStatus = 'processing'`.
 * This prevents duplicate Cloud Function invocations when concurrent callers
 * attempt to render the same patient report simultaneously.
 *
 * Why a transaction is required (not just updateDoc):
 * When a render fails and sets status to 'error', every
 * rerenderAutismPatientReportIfStale instance that was waiting in the
 * isReportProcessing branch wakes on the same snapshot and each calls
 * this trigger within milliseconds. Without a transaction, every caller
 * wins the race and fires a separate Cloud Function -- compounding
 * Google Drive API load during a rate-limit event. The transaction
 * ensures only one caller sets 'processing'; the rest get
 * 'already-processing' and no-op.
 *
 * This mirrors the ADHD pattern in TriggerRenderPatientReport.js.
 *
 * Also writes `prReportApprovedAt` and `prReportProcessingStartedAt` at
 * trigger time so:
 *   - The staleness check in rerenderAutismPatientReportIfStale correctly
 *     detects section changes that occur while the CF is running
 *   - The DocumentsDashboard_Autism self-healing logic can detect and reset
 *     stuck statuses (getStaleAutismReportReset, 15 min)
 *   - The reconcile sweep (reconcile_stuck_reports.py) can replay using the
 *     persisted prReportRenderIntent (12 min)
 *
 * The CF call is fire-and-forget. `deadline-exceeded` errors are already
 * handled correctly here -- the CF continues server-side and will update
 * status itself.
 *
 * @param {Object} params
 * @param {string} params.patientId   - Patient Firestore document ID.
 * @param {string} params.clinicianId - UID of the clinician triggering render.
 * @returns {Promise<{success: boolean, data?: object, error?: any}>}
 */
export const TriggerRenderAutismPatientReport = async ({ patientId, clinicianId }) => {
  const renderAutismPatientReport = httpsCallable(functions, 'renderAutismPatientReport', {
    timeout: 360000,
  });

  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  if (!patientId || !clinicianId) {
    console.error('Missing required parameters:', { patientId, clinicianId });
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    const headerRef = firestoreDoc(db, ...getPatientHeaderPathSegments(patientId));

    // Transactional guard: only one caller wins the race to set 'processing'.
    // Reads the current autismPatientReport status inside the transaction; if
    // already 'processing', we bail out without writing or invoking the CF.
    let alreadyProcessing = false;

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(headerRef);
      const autismPatientReport = snap.data()?.autismPatientReport;

      if (autismPatientReport?.prReportProcessingStatus === 'processing') {
        alreadyProcessing = true;
        return;
      }

      // prReportApprovedAt is set at trigger time (before the CF runs) so
      // rerenderAutismPatientReportIfStale can detect section changes during
      // CF execution. The renderAutismPatientReport CF intentionally does NOT
      // write prReportApprovedAt on success -- the client owns it.
      transaction.update(headerRef, {
        'autismPatientReport.prReportProcessingStatus': 'processing',
        'autismPatientReport.prReportProcessingStartedAt': serverTimestamp(),
        'autismPatientReport.prReportApprovedAt': serverTimestamp(),
        // Persist the minimum needed to replay this render if it gets stuck.
        // The backend reconcile sweep (reconcile_stuck_reports.py) reads this
        // to re-invoke renderAutismPatientReport as the original clinician.
        // Mirrors the ADHD prReportRenderIntent written by
        // TriggerPatientReportGeneration.
        'autismPatientReport.prReportRenderIntent': { clinicianId },
      });
    }, { maxAttempts: 10 });

    if (alreadyProcessing) {
      console.log('Skipping Autism patient report render — already processing');
      return { success: true, data: { status: 'already-processing' } };
    }

    // Fire and forget: the CF sets 'complete' or 'error' on the header.
    renderAutismPatientReport({
      patientId,
      clinicianId,
    }).then((result) => {
      console.log('Autism Patient Report generated successfully:', result.data);
    }).catch((error) => {
      console.error('Error in renderAutismPatientReport:', error);

      // deadline-exceeded: client timed out but CF may still be running
      if (error.code === 'deadline-exceeded') {
        console.log('Client-side timeout; function continues in background');
        return;
      }

      updateDoc(headerRef, {
        'autismPatientReport.prReportProcessingStatus': 'error',
      }).catch((updateError) => {
        console.error('Error updating patient report processing status:', updateError);
      });
    });

    return { success: true, data: { status: 'processing' } };
  } catch (error) {
    console.error('Error triggering Autism Patient Report render:', error);
    return { success: false, error };
  }
};
